import React, { useState, useEffect } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import {
  normalizeBrokerReportProperties,
  revertNormalizeDate,
} from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import DatePicker from "../../components/Forms/DatePicker/DatePicker.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { useLocation } from "react-router-dom";
import MultiSelect, { Option } from "../../components/Forms/MultiSelect.tsx";
import BrokerReportDataGrid from "../../components/DataTables/BrokerReportDataGrid.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";

interface BrokerReportResult {
  PremiumSum?: string;
  GFSum?: string;
  StickerSum?: string;
  "2PercSum"?: string;
  CommissionSum?: string;
  Contracts?: string;
  AllTrans?: string;
  [key: string]: any;
}

const labelClass =
  "mb-1 ml-1 block text-xs font-medium text-black dark:text-white";

const BrokerReport: React.FC = () => {
  const [formState, setFormState] = useState({
    fromDate: "",
    toDate: "",
    company: "",
    policyType: "",
    agent: "",
    selectedOffices: [],
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [agentOptions, setAgentOptions] = useState<{ [key: string]: string }>(
    {}
  );
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [offices, setOffices] = useState<{ [key: string]: string }>({});
  const [officesOptions, setOfficesOptions] = useState<Option[]>([]);
  const [selectedOfficeValues, setSelectedOfficeValues] = useState<string[]>(
    []
  );
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [policies, setPolicies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<any | null>(null);

  const location = useLocation();

  useEffect(() => {
    setFormState({
      fromDate: "",
      toDate: "",
      company: "",
      policyType: "",
      agent: "",
      selectedOffices: [],
    });
    setPolicies([]);
    setAlert(null);
    setAlertType(undefined);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [agents, companies, officesRes, policyTypesRes] =
          await Promise.all([
            InsuranceService.getAllAgents(jwt),
            InsuranceService.getAllInsuranceCompanies(jwt),
            InsuranceService.getAllOffices(jwt),
            InsuranceService.getAllPolicyTypes(jwt),
          ]);
        setAgentOptions(agents as { [key: string]: string });
        setInsuranceCompanies(companies as { [key: string]: string });
        setOffices(officesRes as { [key: string]: string });
        setPolicyTypes(policyTypesRes as { [key: string]: string });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  useEffect(() => {
    const updatedOfficeOptions = Object.entries(offices).map((office) => ({
      text: office[1],
      value: office[0],
      id: office[0],
      selected: selectedOfficeValues.includes(office[0]),
    }));
    setOfficesOptions(updatedOfficeOptions);
  }, [offices, selectedOfficeValues]);

  const handleOfficeSelectionChange = (selectedValues: string[]) => {
    setSelectedOfficeValues(selectedValues);
    setFormState((prevState) => ({
      ...prevState,
      selectedOffices: selectedValues,
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.fromDate) {
      setAlert("Моля, въведете стартова дата");
      setAlertType(FadeAlertType.error);
      return;
    }
    try {
      setIsLoading(true);
      const result: BrokerReportResult = await PolicyService.fetchBrokerReport(
        { ...formState, fromDate: revertNormalizeDate(formState.fromDate) },
        jwt
      );
      const {
        PremiumSum,
        GFSum,
        StickerSum,
        ["2PercSum"]: TaxSum,
        CommissionSum,
        Contracts,
        AllTrans,
        ...policyData
      } = result;
      const cleanedData = {
        PremiumSum: PremiumSum?.split(":")[1]?.trim(),
        GFSum: GFSum?.split(":")[1]?.trim(),
        StickerSum: StickerSum?.split(":")[1]?.trim(),
        TaxSum: TaxSum?.split(":")[1]?.trim(),
        CommissionSum: CommissionSum?.split(":")[1]?.trim(),
        Contracts: Contracts?.split(":")[1]?.trim(),
        AllTrans: AllTrans?.split(":")[1]?.trim(),
      };
      const normalizedPolicies = Object.values(policyData)
        .filter((policy) => typeof policy === "object")
        .map((policy) => normalizeBrokerReportProperties(policy));
      setPolicies(normalizedPolicies);
      setSummary(cleanedData);
    } catch (error) {
      setAlert("Грешка при зареждането на полици.");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
    setAlert(null);
  };

  return (
    <>
      <Breadcrumb pageName="Дневник на брокера" />

      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Дневник на брокера" />

        <form onSubmit={handleSearch} className="p-3">
          {/* 2 rows × 3 cols */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <DatePicker
                label="От дата"
                name="fromDate"
                onChange={handleInputChange}
                value={formState.fromDate}
              />
            </div>
            <div>
              <DatePicker
                label="До дата"
                name="toDate"
                onChange={handleInputChange}
                value={formState.toDate}
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.company}
                placeholder="Изберете компания"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, company: value }))
                }
                name="company"
              />
            </div>
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.policyType}
                placeholder="Изберете вид полица"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, policyType: value }))
                }
                name="policyType"
              />
            </div>
            <div>
              <label className={labelClass}>Агент</label>
              <SelectGroup
                value={formState.agent}
                placeholder="Изберете агент"
                options={agentOptions}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, agent: value }))
                }
                name="agent"
              />
            </div>
            <div>
              <label className={labelClass}>Офис</label>
              <div className="text-xs [&>div]:min-h-0 [&>div]:py-0 [&>div]:text-xs [&_button]:text-xs [&_input]:text-xs [&_li]:text-xs [&_span]:text-xs [&_ul]:text-xs">
                <MultiSelect
                  options={officesOptions}
                  setOptions={setOfficesOptions}
                  placeholder="Изберете офис"
                  onSelectionChange={handleOfficeSelectionChange}
                  id="multi-select-office"
                />
              </div>
            </div>
          </div>

          {/* Button - center column of third row */}
          <div className="mt-2 grid grid-cols-3 gap-2">
            <div className="col-start-2">
              <button
                type="submit"
                className="bg-primary w-full justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
              >
                Покажи
              </button>
            </div>
          </div>
        </form>

        <BrokerReportDataGrid policies={policies} isLoading={isLoading} />

        {summary && (
          <div className="mb-3 grid grid-cols-2 gap-2 rounded-md bg-gray-100 p-3 dark:bg-gray-800 md:grid-cols-4">
            <div className="text-xs text-gray-700 dark:text-gray-300">
              Сума на премиите: <strong>{summary.PremiumSum}</strong>
            </div>
            <div className="text-xs text-gray-700 dark:text-gray-300">
              Сума за ГФ: <strong>{summary.GFSum}</strong>
            </div>
            <div className="text-xs text-gray-700 dark:text-gray-300">
              Сума за Стикери: <strong>{summary.StickerSum}</strong>
            </div>
            <div className="text-xs text-gray-700 dark:text-gray-300">
              Сума Данък 2%: <strong>{summary.TaxSum}</strong>
            </div>
            <div className="text-xs text-gray-700 dark:text-gray-300">
              Сума комисион: <strong>{summary.CommissionSum}</strong>
            </div>
            <div className="text-xs text-gray-700 dark:text-gray-300">
              Договори: <strong>{summary.Contracts}</strong>
            </div>
            <div className="text-xs text-gray-700 dark:text-gray-300">
              Общ брой: <strong>{summary.AllTrans}</strong>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BrokerReport;
